import React, { useState } from 'react'
import { DatePicker, Space, Button, Tooltip, Checkbox } from 'antd'
import { Input } from '@/ui'
import { useSelector } from 'dva'
import moment from 'moment'
import styles from './index.less'
import classNames from 'classnames'
import QuestionCircleFilled from '@ant-design/icons/QuestionCircleFilled'

export const RangeScopeConfig = {
    TODAY: {
        label: '今天',
        value: 'TODAY',
        getRangeValue: () => [moment().startOf('day'), moment().endOf('day')]
    },
    YESTERDAY: {
        label: '昨天',
        value: 'YESTERDAY',
        getRangeValue: () => [moment().subtract(1, 'day').startOf('day'), moment().subtract(1, 'day').endOf('day')]
    },
    THIS_WEEK: {
        label: '本周',
        value: 'THIS_WEEK',
        getRangeValue: () => [moment().startOf('week'), moment().endOf('week')]
    },
    CURRENT_MONTH: {
        label: '本月',
        value: 'CURRENT_MONTH',
        getRangeValue: () => [moment().startOf('month'), moment().endOf('month')]
    },
    LAST_30_DAYS: {
        label: '近30天',
        value: 'LAST_30_DAYS',
        getRangeValue: () => [moment().subtract(30, 'days').startOf('day'), moment().endOf('day')]
    },
    LAST_60_DAYS: {
        label: '近60天',
        value: 'LAST_60_DAYS',
        getRangeValue: () => [moment().subtract(60, 'days').startOf('day'), moment().endOf('day')]
    },
    LAST_90_DAYS: {
        label: '近90天',
        value: 'LAST_90_DAYS',
        // eslint-disable-next-line no-magic-numbers
        getRangeValue: () => [moment().subtract(90, 'days').startOf('day'), moment().endOf('day')]
    }
} as const
/** 不需要默认开始和结束时间的coId */
const NOT_DEFAULT_SHOWTIME_COID = ['12860251']

export type PresetDateRangeType = (typeof RangeScopeConfig)[keyof typeof RangeScopeConfig]['value']

export const getScopeDynamicRangeValue = ({
    dynamicTimeRangeScope,
    startFormat = 'YYYY-MM-DD HH:mm:ss',
    endFormat = 'YYYY-MM-DD HH:mm:ss'
}) => {
    if (!RangeScopeConfig[dynamicTimeRangeScope]) {
        return
    }
    const [startTime, endTime] = RangeScopeConfig[dynamicTimeRangeScope].getRangeValue()
    return {
        startTime: startTime.format(startFormat),
        endTime: endTime.format(endFormat)
    }
}

const defaultScopeConfig = [
    RangeScopeConfig.TODAY,
    RangeScopeConfig.YESTERDAY,
    RangeScopeConfig.THIS_WEEK,
    RangeScopeConfig.CURRENT_MONTH,
    RangeScopeConfig.LAST_30_DAYS,
    RangeScopeConfig.LAST_60_DAYS
]

export const DateScope = ({
    scopeConfig = defaultScopeConfig,
    scopeDynamicSupport = false,
    dynamic,
    onDynamicChange,
    onChange
}) => {
    return (
        <Space>
            {scopeDynamicSupport && (
                <div>
                    <Checkbox checked={dynamic} onChange={e => onDynamicChange?.(e.target.checked)}>
                        时间动态变动
                    </Checkbox>
                    <Tooltip overlay={null} placement="top" title="选中的时间段随日期变化而移动">
                        <QuestionCircleFilled
                            style={{
                                color: '#999',
                                fontSize: 14,
                                cursor: 'pointer'
                            }}
                        />
                    </Tooltip>
                </div>
            )}
            {scopeConfig.map((item, index) => {
                return (
                    <Button key={index} type="link" style={{ padding: 0 }} onClick={() => onChange?.(item)}>
                        {item.label}
                    </Button>
                )
            })}
        </Space>
    )
}

export default ({
    value,
    onChange,
    placeholder = ['开始时间', '结束时间'],
    format = 'YYYY-MM-DD HH:mm:ss',
    startFormat: propStartFormat,
    endFormat: propEndFormat,
    showTime = true,
    disabled = false,
    leftDisabled = false,
    rightDisabled = false,
    allowClear = true,
    splitStyle,
    pickerStyle,
    columnLayout = false,
    rangeDays,
    quick = true, // 快捷选择时间范围
    scopeConfig = defaultScopeConfig,
    scopeDynamicSupport, // 是否展示支持动态/非动态切换
    defaultScopeDynamic = false, // 是否默认动态
    showToday = undefined,
    ...restProps
}) => {
    const { currentUser } = useSelector(state => state?.user) || {}
    const [startOpen, setStartOpen] = useState(false)
    const [endOpen, setEndOpen] = useState(false)
    const [tempCurrentScope, setTempCurrentScope] = useState(null)
    // 临时勾选快捷时间范围动态
    const [tempCheckScopeDynamic, setTempCheckScopeDynamic] = useState(defaultScopeDynamic)

    const startFormat = propStartFormat || format
    const endFormat = propEndFormat || format

    const startTime = value?.startTime ? moment(value.startTime, startFormat) : undefined
    const endTime = value?.endTime ? moment(value.endTime, endFormat) : undefined
    const checkScopeDynamic = !!(value?.dynamicTimeRangeScope || tempCheckScopeDynamic)
    const currentScope = value?.dynamicTimeRangeScope ? RangeScopeConfig[value.dynamicTimeRangeScope] : tempCurrentScope

    const changeHandle = (type, date) => {
        setTempCurrentScope(null)
        if (type === 'left') {
            onChange({
                endTime: value?.endTime,
                startTime: date ? date.format(startFormat) : undefined
            })
            return
        }
        if (type === 'right') {
            onChange({
                startTime: value?.startTime,
                endTime: date ? date.format(endFormat) : undefined
            })
            return
        }

        if (type === 'block') {
            onChange({
                startTime: date ? date.format(startFormat) : undefined,
                endTime: undefined
            })
            return
        }
    }

    const disabledDate = current => {
        if ((startTime || endTime) && rangeDays) {
            const tooEarly = startTime && Math.abs(startTime.diff(current, 'days')) > rangeDays

            const tooLate = endTime && Math.abs(current.diff(endTime, 'days')) > rangeDays

            if (tooEarly || tooLate) {
                return true
            }
        }

        return false
    }

    const triggerScopeChange = ({ dynamic, scopeItem }) => {
        setTempCheckScopeDynamic(dynamic)
        const [start, end] = scopeItem.getRangeValue?.() || []
        if (dynamic) {
            onChange({
                startTime: start ? start.format(startFormat) : undefined,
                endTime: end ? end.format(endFormat) : undefined,
                dynamicTimeRangeScope: scopeItem.value,
                startFormat,
                endFormat
            })
        } else {
            onChange({
                startTime: start ? start.format(startFormat) : undefined,
                endTime: end ? end.format(endFormat) : undefined
            })
        }
    }

    const onDateScopeChange = scopeItem => {
        setTempCurrentScope(scopeItem)
        triggerScopeChange({
            dynamic: checkScopeDynamic,
            scopeItem: scopeItem
        })
        setStartOpen(false)
        setEndOpen(false)
    }

    const onDynamicChange = newDynamic => {
        triggerScopeChange({
            dynamic: newDynamic,
            scopeItem: currentScope
        })
    }
    const getDefaultTime = type => {
        if (NOT_DEFAULT_SHOWTIME_COID.includes(currentUser?.coId)) {
            return true
        }
        if (type === 'left') {
            return { defaultValue: moment('00:00:00', 'HH:mm:ss') }
        }
        if (type === 'right') {
            return { defaultValue: moment('23:59:59', 'HH:mm:ss') }
        }
    }

    const renderExtraFooter = () => {
        if (quick) {
            return (
                <DateScope
                    scopeConfig={scopeConfig}
                    scopeDynamicSupport={scopeDynamicSupport}
                    dynamic={checkScopeDynamic}
                    onChange={onDateScopeChange}
                    onDynamicChange={onDynamicChange}
                />
            )
        }
        return null
    }

    if (value?.dynamicTimeRangeScope) {
        const { label = '' } = RangeScopeConfig[value.dynamicTimeRangeScope] ?? {}

        return (
            <div {...restProps}>
                <DatePicker
                    className={styles.blockPicker}
                    value={startTime}
                    disabled={disabled}
                    onChange={date => changeHandle('block', date)}
                    disabledDate={disabledDate}
                    suffixIcon={null}
                    showNow={false}
                    showTime={showTime}
                    allowClear={allowClear}
                    open={startOpen}
                    onOpenChange={() => setStartOpen(!startOpen)}
                    renderExtraFooter={renderExtraFooter}
                    format={() => label}
                    showToday={showToday}
                />
            </div>
        )
    }

    return (
        <div {...restProps}>
            <Input.Group
                className={classNames(styles.dateWrapper, columnLayout ? styles.columnLayout : styles.rowLayout)}
            >
                <DatePicker
                    className={styles.leftPicker}
                    value={startTime}
                    disabled={disabled || leftDisabled}
                    placeholder={placeholder[0]}
                    onChange={date => changeHandle('left', date)}
                    disabledDate={disabledDate}
                    suffixIcon={null}
                    showNow={false}
                    showTime={showTime ? getDefaultTime('left') : showTime}
                    allowClear={allowClear}
                    style={pickerStyle}
                    open={startOpen}
                    onOpenChange={() => setStartOpen(!startOpen)}
                    renderExtraFooter={renderExtraFooter}
                    showToday={showToday}
                />
                {!columnLayout && (
                    <Input
                        className={classNames({
                            [styles.split]: true,
                            [styles.disabled]: disabled
                        })}
                        style={splitStyle}
                        prefix={<div className={styles.prefix} />}
                        placeholder=""
                        disabled
                        defaultValue=" "
                    />
                )}
                <DatePicker
                    className={styles.rightPicker}
                    disabledDate={disabledDate}
                    value={endTime}
                    disabled={disabled || rightDisabled}
                    placeholder={placeholder[1]}
                    onChange={date => changeHandle('right', date)}
                    suffixIcon={null}
                    showNow={false}
                    showTime={showTime ? getDefaultTime('right') : showTime}
                    allowClear={allowClear}
                    style={pickerStyle}
                    open={endOpen}
                    onOpenChange={() => setEndOpen(!endOpen)}
                    renderExtraFooter={renderExtraFooter}
                    showToday={showToday}
                />
            </Input.Group>
        </div>
    )
}
